import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatFCFA, currentMonth } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Building2, Home, Users, Wallet, TrendingUp, TrendingDown, CircleDollarSign, Percent } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import DevDashboard from "@/pages/DevDashboard";

const TONES = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
};

const KPI = ({ icon: Icon, label, value, sub, tone = "emerald", testid }) => (
  <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white hover:shadow-md transition-shadow duration-200" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${TONES[tone] || TONES.emerald}`}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
    </div>
    <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{label}</div>
    <div className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const isDevGlobal = user?.role === "dev" && !user?.impersonating;

  const [summary, setSummary] = useState(null);
  const [loyers, setLoyers] = useState({ mois: currentMonth(), rows: [] });

  useEffect(() => {
    if (isDevGlobal) return;
    api.get("/rapports/summary").then((r) => setSummary(r.data));
    api.get("/rapports/loyers-attendus").then((r) => setLoyers(r.data));
  }, [isDevGlobal]);

  if (isDevGlobal) return <DevDashboard />;

  return (
    <div>
      <PageHeader
        title="Tableau de bord"
        subtitle="Vue d'ensemble de votre portefeuille immobilier — propriétés, occupation et flux financiers."
        testid="page-dashboard"
        accent="emerald"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPI icon={Building2} label="Maisons" value={summary?.total_maisons ?? "—"} sub={`${summary?.maisons_occupees ?? 0} occupées · ${summary?.maisons_disponibles ?? 0} libres`} tone="indigo" testid="kpi-maisons" />
        <KPI icon={Percent} label="Taux d'occupation" value={`${summary?.occupancy_rate ?? 0}%`} sub="Sur l'ensemble du parc" tone="emerald" testid="kpi-occupation" />
        <KPI icon={TrendingUp} label="Revenu du mois" value={formatFCFA(summary?.revenu_mois ?? 0)} sub={summary?.mois_courant} tone="teal" testid="kpi-revenu" />
        <KPI icon={TrendingDown} label="Dépenses du mois" value={formatFCFA(summary?.depenses_mois ?? 0)} sub={summary?.mois_courant} tone="rose" testid="kpi-depenses" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <KPI icon={Users} label="Propriétaires" value={summary?.total_proprietaires ?? "—"} tone="amber" testid="kpi-proprietaires" />
        <KPI icon={Home} label="Locataires actifs" value={summary?.total_locataires_actifs ?? "—"} tone="violet" testid="kpi-locataires" />
        <KPI icon={CircleDollarSign} label="Bénéfice du mois" value={formatFCFA(summary?.benefice_mois ?? 0)} sub="Revenu - Dépenses" tone="emerald" testid="kpi-benefice" />
      </div>

      <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="card-loyers-attendus">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Suivi des loyers</div>
            <h2 className="font-heading text-xl font-bold text-slate-900">Loyers attendus — {loyers.mois}</h2>
          </div>
          <Wallet className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="rounded-xl overflow-hidden border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Locataire</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maison</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Attendu</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Payé</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Solde</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loyers.rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">Aucun locataire actif.</TableCell></TableRow>
              )}
              {loyers.rows.map((r) => (
                <TableRow key={r.locataire_id} className="hover:bg-slate-50/60">
                  <TableCell className="font-medium text-slate-900">{r.locataire_nom}</TableCell>
                  <TableCell className="text-slate-600">{r.maison_nom}</TableCell>
                  <TableCell className="text-right">{formatFCFA(r.attendu)}</TableCell>
                  <TableCell className="text-right">{formatFCFA(r.paye)}</TableCell>
                  <TableCell className={`text-right font-medium ${r.solde > 0 ? "text-rose-600" : "text-emerald-700"}`}>{formatFCFA(r.solde)}</TableCell>
                  <TableCell>
                    {r.statut === "payé" && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Payé</Badge>}
                    {r.statut === "partiel" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200">Partiel</Badge>}
                    {r.statut === "impayé" && <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 border border-rose-200">Impayé</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
